import { Component, Input, OnInit, OnChanges, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Keyboard } from '@awesome-cordova-plugins/keyboard/ngx';
import { Platform, IonInfiniteScroll, IonSearchbar } from '@ionic/angular';
import * as moment from 'moment-timezone';
import { Observable } from 'rxjs';

import { IAssetValues, IAssetValuesPosition } from '@interfaces/assetValue';
import { IGeneralConstants } from '@interfaces/general-constants';
import { IDeposit } from '@interfaces/movements';
import { IAssetName } from '@interfaces/portfolio-constants';
import { IUser } from '@interfaces/users';
import { AssetsService } from '@services/assets/assets.service';
import { AuthService } from '@services/auth/auth.service';
import { FirebaseService } from '@services/firebase/firebase.service';
import { NavigationService } from '@services/navigation/navigation.service';

const groupLoad = 10;
const ETF_TYPE = 'ETF';
const STOCK_TYPE = 'EQUITY';

@Component({
  selector: 'app-market',
  templateUrl: './market.page.html',
  styleUrls: ['./market.page.scss'],
})
export class MarketPage implements OnInit, OnChanges {
  @ViewChild(IonSearchbar, {static: false}) searchBar: IonSearchbar;
  @ViewChild(IonInfiniteScroll, {static: false}) infiniteScroll: IonInfiniteScroll;
  @Input() favoritesOnlyMiddleColumn: boolean;
  @Input() inMiddleColumn: boolean;
  @Input() selected: string;
  assets: IAssetName[];
  assetValuesMap: { [assetId: string]: number };
  loaded: boolean;
  selectedPeriod: string;
  unreadDeposits: IDeposit[];
  unreadDepositsCount: number;
  assetValues: IAssetValues;
  activeFirebaseCampaign: string;
  searchbarActivated: boolean;
  textSearch: string;
  filteredAssets: IAssetName[];
  displayAssets: IAssetName[];
  ownedAssets: string[];
  favoriteAssets: string[];
  filters = {
    etfs: false,
    stocks: false,
    owned: false,
    favorites: false,
  };
  backButtonText: string;
  favoritesOnly: boolean;
  assetValuesPosition: IAssetValuesPosition[];
  noDWAccess: boolean;

  constructor(
    private route: ActivatedRoute,
    private firebaseService: FirebaseService,
    private assetService: AssetsService,
    private authService: AuthService,
    private router: Router,
    private keyboard: Keyboard,
    private platform: Platform,
  ) {
    this.backButtonText = this.platform.is('ios') ? 'Atrás' : '';
    this.assetValuesMap = {};
    this.loaded = false;
    this.selectedPeriod = '1D';
    moment.locale('es');
    this.textSearch = '';
    this.favoriteAssets = [];
    this.favoritesOnly = this.route.snapshot.queryParamMap.get('favoritesOnly') === 'true';
    this.filters.favorites = this.favoritesOnly;
  }

  ngOnChanges(changes) {
    if (changes.favoritesOnlyMiddleColumn) {
      this.favoritesOnly = this.favoritesOnlyMiddleColumn;
      this.filters = {
        etfs: false,
        stocks: false,
        owned: false,
        favorites: this.favoritesOnly,
      };
      if (this.assets && this.assets.length) {
        this.updateFilterText({ target: { value: this.textSearch } });
      }
    }
  }

  async ngOnInit() {
    this.assetService.getAssetValues();
    const marketAssets = await this.assetService.getMarketAssets();
    const dwAssets = await this.assetService.getDWAssets();
    this.assets = [...marketAssets, ...dwAssets].sort((a, b) => a.assetId > b.assetId ? 1 : -1);
    Observable.combineLatest([
      this.authService.currentUserSource,
      this.assetService.currentAssetValues,
      this.firebaseService.currentGeneralConstants,
      this.assetService.currentFavoriteAssets,
    ]).subscribe(async ([userInfo, assetValues, generalConstants, favoriteAssets]:
      [IUser, IAssetValues, IGeneralConstants, string[]]) => {
      if (assetValues && generalConstants) {
        this.assetValues = assetValues;
        this.assetValuesPosition = assetValues.values;
        if (!this.displayAssets) {
          this.updateFilterText({ target: { value: this.textSearch } });
          this.changePeriod({ target: { value: this.selectedPeriod } });
        }
        const isFree = userInfo && userInfo.subscription !== 'pro';
        const dwAccess = !userInfo || userInfo.drivewealthAccess !== false;
        const hasInvestments = userInfo && userInfo.lastHistoricEvolution &&
                               userInfo.lastHistoricEvolution.totalAmount > 0 &&
                               userInfo.lastHistoricEvolution.totalAmount !==
                               userInfo.lastHistoricEvolution.effectivePortfolioComposition[0].amount;
        this.activeFirebaseCampaign = generalConstants.campaignBannerInPage.market &&
                                      (!generalConstants.campaignBannerInPage.onlyActive || hasInvestments) &&
                                      (!generalConstants.campaignBannerInPage.onlyPassive || !hasInvestments) &&
                                      (!generalConstants.campaignBannerInPage.onlyDW || dwAccess) &&
                                      (!generalConstants.campaignBannerInPage.onlyUnsubscribed || isFree) &&
                                      generalConstants.activeFirebaseCampaign;
        assetValues.values.forEach(assetValue => {
          const asset = this.assets.find(a => a.assetId === assetValue.assetId);
          if (asset) {
            this.assetValuesMap[assetValue.assetId] = asset.drivewealthAsset ?
              assetValue.sharePriceOriginalCurrency :
              assetValue.sharePrice;
          }
        });
        if (userInfo) {
          this.noDWAccess = userInfo.drivewealthAccess === false;
          if (JSON.stringify(this.favoriteAssets) !== JSON.stringify(favoriteAssets)) {
            this.favoriteAssets = favoriteAssets;
            await this.updateFilterText({ target: { value: this.textSearch } });
          }
        }
        if (userInfo && userInfo.lastHistoricEvolution && userInfo.lastHistoricEvolution.effectivePortfolioComposition) {
          this.ownedAssets = userInfo.lastHistoricEvolution.effectivePortfolioComposition.filter(a => a.amount > 0).map(a => a.assetId);
        }
      }
    });
    this.loaded = true;
  }

  sortByReturn() {
    this.filteredAssets.forEach(asset => {
      asset.returns = this.assetValuesPosition.find(a => a.assetId === asset.assetId);
    });
    const period = 'return' + this.selectedPeriod;
    this.filteredAssets.sort((a, b) => {
      if (b.returns && a.returns) {
        return b.returns[period] - a.returns[period];
      }
    });
    this.displayAssets = this.filteredAssets.slice(0, groupLoad);
  }

  changePeriod($event) {
    this.selectedPeriod = $event.target.value;
    this.sortByReturn();
  }

  goToAsset(assetId: string) {
    this.keyboard.hide();
    this.router.navigate(['asset-details', assetId]);
  }

  updatedAtText() {
    return this.assetValues ? 'Actualizado ' + moment(this.assetValues.updatedAt.toDate()).fromNow() : '';
  }

  toggleSearchBar() {
    this.searchbarActivated = !this.searchbarActivated;
    if (this.searchbarActivated) {
      this.searchBar.setFocus();
    } else {
      this.searchBar.value = '';
    }
  }

  updateFilterText(event) {
    this.infiniteScroll.disabled = false;
    const textLookUp = event.target.value.toLowerCase();
    this.filters.favorites = this.favoritesOnly ? !textLookUp : this.filters.favorites;
    this.filteredAssets = !textLookUp ? this.assets : this.assets.filter(a => {
      return `${a.assetId} ${a.assetClass} ${a.name}`.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
             .includes(textLookUp.normalize('NFD').replace(/[\u0300-\u036f]/g, ''));
    });
    const noFilters = (!this.filters.favorites && this.filters.etfs && this.filters.stocks
                      && !this.filters.owned) ||
                      (!this.filters.favorites && !this.filters.etfs && !this.filters.stocks
                      && !this.filters.owned);
    this.filteredAssets = noFilters ? this.filteredAssets : this.filteredAssets.filter(a => {
      const showFavorite = (this.filters.favorites && this.favoriteAssets.includes(a.assetId));
      if (this.filters.favorites && !showFavorite) { return false; }
      const showOwned = (this.filters.owned && this.ownedAssets.includes(a.assetId));
      if (this.filters.owned && !showOwned) { return false; }
      const showStock = (this.filters.stocks && a.type === STOCK_TYPE);
      const showETF = (this.filters.etfs && a.type === ETF_TYPE);
      return showETF || showStock || (!this.filters.stocks && !this.filters.etfs);
    });
    this.sortByReturn();
    this.displayAssets = this.filteredAssets.slice(0, groupLoad);
    this.textSearch = textLookUp;
    this.showMoreAssets();
  }

  showMoreAssets(event?) {
    if (this.displayAssets.length < this.filteredAssets.length) {
      this.displayAssets.push(...this.filteredAssets.slice(this.displayAssets.length, this.displayAssets.length + groupLoad));
      setTimeout(() => {
        this.infiniteScroll.complete();
      });
    } else {
      this.infiniteScroll.complete();
      this.infiniteScroll.disabled = true;
    }
  }

  isCenterWithMiddleColumn() {
    return window.innerWidth > 800 && !this.inMiddleColumn;
  }

  goToAddFavorites() {
    this.firebaseService.logEvent('clicked_go_to_add_favorites', {});
    this.toggleSearchBar();
  }
}
